// src/pages/myjobs/MyJobsPage.tsx
import React, { useContext } from "react";
import { useQuery, useMutation } from "@apollo/client";
import {
  Box,
  Tabs,
  Tab,
  Stack,
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  TextField,
  Switch,
  FormControlLabel,
  Divider,
  Chip,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  ListItemText,
} from "@mui/material";
import UploadFileRoundedIcon from "@mui/icons-material/UploadFileRounded";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";

import JobCard from "../components/jobCard";
import useJobActions from "../hooks/useJobActions";
import {
  MY_CANDIDATE_PROFILE,
  MY_JOB_APPS,
  MY_SAVED_JOBS,
} from "../../../../platform/graphql/queries/careJobs";
import {
  UPDATE_CANDIDATE_PROFILE,
  UPDATE_JOB_APPLICATION,
} from "../../../../platform/graphql/mutations/careJobs";
import { GENERATE_UPLOAD_URL } from "../../../../platform/graphql/mutations/image";
import { uploadToGCS } from "../../../../platform/util/upload/uploadToGCS";
import { UserContext } from "../../../../platform/components/auth/auth-context";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import {
  ApplicantSubscriptionRecord,
  getApplicantSubscriptionStatusLabel,
  getLatestApplicantSubscription,
} from "../api/applicantSubscriptions";

/* ---------------------------------------------
 * Helpers
 * --------------------------------------------- */
function fmtDateTime(iso?: string | null) {
  try {
    return iso ? new Date(iso).toLocaleString() : "-";
  } catch {
    return "-";
  }
}

function useMostRecentFromApps(apps: any[]) {
  const withResume = (apps || [])
    .filter((a) => a?.resumeUrl)
    .map((a) => ({
      url: String(a.resumeUrl),
      ts: Number(new Date(a.updatedAt || a.createdAt || 0)),
    }))
    .sort((a, b) => b.ts - a.ts);

  const withCover = (apps || [])
    .filter((a) => a?.coverLetter)
    .map((a) => ({
      text: String(a.coverLetter),
      ts: Number(new Date(a.updatedAt || a.createdAt || 0)),
    }))
    .sort((a, b) => b.ts - a.ts);

  return {
    mostRecentResumeUrl: withResume[0]?.url || "",
    mostRecentCover: withCover[0]?.text || "",
  };
}

const fileNameFromUrl = (url?: string | null) => {
  if (!url) return "";
  try {
    const last = decodeURIComponent(url.split("/").pop() || "");
    const base = (last.split("?")[0] || "").trim();
    return base || "";
  } catch {
    return "";
  }
};

// Acceptable resume file types
const ACCEPT_MIME =
  ".pdf,application/pdf,.doc,application/msword,.docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document";

/** Curated qualification list for care jobs (edit as needed). */
const QUAL_OPTIONS: string[] = [
  "CNA",
  "HCA",
  "RN",
  "LPN",
  "CPR / First Aid",
  "Nurse Delegation",
  "Medication Aide",
  "Dementia / Memory Care",
  "TB Test",
  "BLS",
  "Food Handler",
  "Driver’s License",
  "Background Check Cleared",
  "COVID Vaccinated",
  "Live-in Care",
  "Overnight",
];

/* ---------------------------------------------
 * Profile Tab (BACKEND-POWERED, with upload + checkbox list)
 * --------------------------------------------- */
function JobProfileTab({ apps }: { apps: any[] }) {
  const { providerUser } = useContext(UserContext) as any;
  const { mostRecentResumeUrl, mostRecentCover } = useMostRecentFromApps(apps);

  const profQ = useQuery(MY_CANDIDATE_PROFILE, {
    fetchPolicy: "cache-and-network",
  });

  const [mutUpdate, { loading: saving }] = useMutation(
    UPDATE_CANDIDATE_PROFILE,
    {
      refetchQueries: [{ query: MY_CANDIDATE_PROFILE }],
      awaitRefetchQueries: true,
    }
  );
  const [mutUpdateApp] = useMutation(UPDATE_JOB_APPLICATION);

  // Signed URL generator for uploading to GCS
  const [mutGenerateUploadUrl] = useMutation(GENERATE_UPLOAD_URL);

  // Local state mirrors backend fields
  const [openToWork, setOpenToWork] = React.useState<boolean>(true);
  const [headline, setHeadline] = React.useState<string>("");
  const [qualifications, setQualifications] = React.useState<string[]>([]);
  const [phone, setPhone] = React.useState<string>("");

  // Resume: internal URL + user-visible name
  const [resumeUrl, setResumeUrl] = React.useState<string>("");
  const [resumeName, setResumeName] = React.useState<string>("");

  const [coverLetter, setCoverLetter] = React.useState<string>("");
  const [notice, setNotice] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [uploading, setUploading] = React.useState(false);
  const [savedSnapshot, setSavedSnapshot] = React.useState<string | null>(null);

  const buildSnapshot = React.useCallback(
    (draft: {
      openToWork: boolean;
      headline: string;
      phone: string;
      resumeUrl: string;
      resumeName: string;
      coverLetter: string;
      qualifications: string[];
    }) =>
      JSON.stringify({
        openToWork: Boolean(draft.openToWork),
        headline: (draft.headline || "").trim(),
        phone: (draft.phone || "").trim(),
        resumeUrl: (draft.resumeUrl || "").trim(),
        resumeName: (draft.resumeName || "").trim(),
        coverLetter: (draft.coverLetter || "").trim(),
        qualifications: Array.from(
          new Set(
            (draft.qualifications || [])
              .map((q) => String(q || "").trim())
              .filter(Boolean)
          )
        ).sort(),
      }),
    []
  );

  // Hydrate from backend once loaded
  React.useEffect(() => {
    const p = profQ.data?.myCandidateProfile;
    if (!p) return;

    const nextOpenToWork = Boolean(p.openToWork);
    const nextHeadline = p.headline ?? "";
    const nextQualifications = Array.isArray(p.qualifications) ? p.qualifications : [];
    const nextPhone = p.phone ?? providerUser?.phone ?? "";
    const nextResumeUrl = p.resume?.url ?? mostRecentResumeUrl ?? "";
    const nextResumeName = p.resume?.name ?? fileNameFromUrl(nextResumeUrl) ?? "";
    const nextCoverLetter = p.defaultCoverLetter ?? mostRecentCover ?? "";

    setOpenToWork(nextOpenToWork);
    setHeadline(nextHeadline);
    setQualifications(nextQualifications);
    setPhone(nextPhone);
    setResumeUrl(nextResumeUrl);
    setResumeName(nextResumeName);
    setCoverLetter(nextCoverLetter);
    setSavedSnapshot(
      buildSnapshot({
        openToWork: nextOpenToWork,
        headline: nextHeadline,
        phone: nextPhone,
        resumeUrl: nextResumeUrl,
        resumeName: nextResumeName,
        coverLetter: nextCoverLetter,
        qualifications: nextQualifications,
      })
    );
  }, [
    profQ.data?.myCandidateProfile,
    providerUser?.phone,
    mostRecentResumeUrl,
    mostRecentCover,
    buildSnapshot,
  ]);

  const currentSnapshot = React.useMemo(
    () =>
      buildSnapshot({
        openToWork,
        headline,
        phone,
        resumeUrl,
        resumeName,
        coverLetter,
        qualifications,
      }),
    [
      buildSnapshot,
      coverLetter,
      headline,
      openToWork,
      phone,
      qualifications,
      resumeName,
      resumeUrl,
    ]
  );

  React.useEffect(() => {
    if (savedSnapshot === null) {
      setSavedSnapshot(currentSnapshot);
    }
  }, [currentSnapshot, savedSnapshot]);

  const hasProfileChanges = savedSnapshot !== null && currentSnapshot !== savedSnapshot;

  const name = providerUser?.username || providerUser?.name || "";
  const email = providerUser?.email || "";
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const onPickFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputEl = e.currentTarget; // capture DOM node before await
    const file = inputEl.files?.[0] || null;
    if (!file) return;

    try {
      setUploading(true);
      setError(null);
      const url = await uploadToGCS(file, mutGenerateUploadUrl);
      setResumeUrl(url || "");
      setResumeName(file.name || fileNameFromUrl(url || "") || "");
    } catch (err: any) {
      setError(err?.message || "Failed to upload resume.");
    } finally {
      setUploading(false);
      if (inputEl) inputEl.value = ""; // reset native input
    }
  };

  const save = async () => {
    setError(null);
    try {
      const trimmedUrl = (resumeUrl || "").trim();
      const trimmedName = (resumeName || "").trim();
      const resumeInput = trimmedUrl
        ? {
          url: trimmedUrl,
          name: trimmedName || fileNameFromUrl(trimmedUrl) || undefined,
        }
        : null;

      const input = {
        openToWork,
        headline: headline.trim() || null,
        phone: phone.trim() || null,
        resume: resumeInput || null,
        defaultCoverLetter: coverLetter.trim() || null,
        qualifications, // selected list from checkbox list box
      };

      const { data } = await mutUpdate({ variables: { input } });
      const ok = data?.updateCandidateProfile?.success;
      if (!ok)
        throw new Error(
          data?.updateCandidateProfile?.message || "Save failed"
        );

      const serverName =
        data?.updateCandidateProfile?.profile?.resume?.name || "";
      const finalResumeName =
        serverName || trimmedName || fileNameFromUrl(trimmedUrl) || "";
      if (serverName) setResumeName(serverName);

      // Keep existing applications in sync so AFH finder can detect resume immediately.
      const resumeForSync = (trimmedUrl || data?.updateCandidateProfile?.profile?.resume?.url || "").trim();
      let syncedCount = 0;
      if (resumeForSync) {
        const missingResumeApps = (apps || []).filter((a: any) => a?.id && !a?.resumeUrl);
        if (missingResumeApps.length) {
          const results = await Promise.allSettled(
            missingResumeApps.map((a: any) =>
              mutUpdateApp({
                variables: {
                  patch: {
                    id: Number(a.id),
                    resumeUrl: resumeForSync,
                  },
                },
              })
            )
          );
          syncedCount = results.filter((r) => r.status === "fulfilled").length;
        }
      }

      setNotice(
        syncedCount > 0
          ? `Profile saved. Resume synced to ${syncedCount} existing application(s).`
          : "Profile saved. New applications will prefill from this."
      );
      setSavedSnapshot(
        buildSnapshot({
          openToWork,
          headline,
          phone,
          resumeUrl: trimmedUrl,
          resumeName: finalResumeName,
          coverLetter,
          qualifications,
        })
      );
      setTimeout(() => setNotice(null), 2500);
    } catch (e: any) {
      setError(e?.message || "Failed to save profile.");
    }
  };

  return (
    <Card variant="outlined">
      <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
        <Stack spacing={2}>
          {profQ.error && (
            <Alert severity="error">{String(profQ.error.message)}</Alert>
          )}

          <Stack direction="row" alignItems="center" spacing={1}>
            <FormControlLabel
              control={
                <Switch
                  checked={openToWork}
                  onChange={(_, v) => setOpenToWork(v)}
                />
              }
              label="Open to opportunities and alerts"
            />
            <Chip
              size="small"
              label={openToWork ? "Open" : "Not open"}
              color={openToWork ? "success" : "default"}
            />
          </Stack>

          <Typography variant="body2" color="text.secondary">
            Turn this on to keep your caregiver profile ready for job discovery and
            future care-job alert matching.
          </Typography>

          <Divider />

          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField
              label="Name"
              value={name}
              InputProps={{ readOnly: true }}
              sx={{ flex: 1 }}
            />
            <TextField
              label="Email"
              value={email}
              InputProps={{ readOnly: true }}
              sx={{ flex: 1 }}
            />
            <TextField
              label="Phone"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              sx={{ flex: 1 }}
            />
          </Stack>

          <TextField
            label="Headline (optional)"
            placeholder="e.g. CNA with 5+ yrs, dementia care"
            value={headline}
            onChange={(e) => setHeadline(e.target.value)}
          />

          {/* ===== Qualifications list box with checkboxes ===== */}
          <FormControl fullWidth>
            <InputLabel id="qualifications-label">
              Qualifications / certificates
            </InputLabel>
            <Select
              labelId="qualifications-label"
              multiple
              value={qualifications}
              onChange={(e) => {
                const value = e.target.value;
                setQualifications(
                  typeof value === "string"
                    ? value.split(",")
                    : (value as string[])
                );
              }}
              input={<OutlinedInput label="Qualifications / certificates" />}
              renderValue={(selected) => (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {(selected as string[]).map((q) => (
                    <Chip key={q} label={q} variant="outlined" />
                  ))}
                </Box>
              )}
              sx={{ bgcolor: "background.paper" }}
            >
              {QUAL_OPTIONS.map((q) => (
                <MenuItem key={q} value={q}>
                  <Checkbox checked={qualifications.indexOf(q) > -1} />
                  <ListItemText primary={q} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* ===== Resume row: name input + upload/replace button + View link ===== */}
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            alignItems="center"
          >
            <TextField
              label="Resume name"
              placeholder="e.g. Jane_Doe_Resume.pdf"
              value={resumeName}
              onChange={(e) => setResumeName(e.target.value)}
              sx={{ flex: 1 }}
            />

            <input
              ref={fileInputRef}
              id="resume-file-input"
              type="file"
              accept={ACCEPT_MIME}
              style={{ display: "none" }}
              onChange={onPickFile}
            />
            <label htmlFor="resume-file-input">
              <Button
                variant="outlined"
                startIcon={<UploadFileRoundedIcon />}
                component="span"
                disabled={uploading}
              >
                {uploading
                  ? "Uploading…"
                  : resumeUrl
                    ? "Replace file"
                    : "Upload resume"}
              </Button>
            </label>

            {resumeUrl ? (
              <Button
                variant="text"
                component="a"
                href={resumeUrl}
                target="_blank"
                rel="noopener noreferrer"
                startIcon={<OpenInNewRoundedIcon />}
              >
                View
              </Button>
            ) : (
              <Button variant="text" disabled startIcon={<OpenInNewRoundedIcon />}>
                View
              </Button>
            )}
          </Stack>

          {resumeName && (
            <Typography variant="body2" color="text.secondary">
              Current resume: <strong>{resumeName}</strong>
            </Typography>
          )}

          <TextField
            label="Default cover letter"
            value={coverLetter}
            onChange={(e) => setCoverLetter(e.target.value)}
            multiline
            minRows={5}
          />

          {error && <Alert severity="error">{error}</Alert>}
          {saving && <Alert severity="info">Saving…</Alert>}
          {notice && <Alert severity="success">{notice}</Alert>}

          <Stack direction="row" justifyContent="flex-end">
            <Button
              variant="contained"
              onClick={save}
              disabled={saving || profQ.loading || uploading || !hasProfileChanges}
            >
              {saving ? "Saving…" : "Save Profile"}
            </Button>
          </Stack>

          <Typography variant="caption" color="text.secondary">
            Tip: Apply will auto-prefill from this profile (resume, cover
            letter, phone).
          </Typography>
        </Stack>
      </CardContent>
    </Card>
  );
}

/* ---------------------------------------------
 * Page
 * --------------------------------------------- */
export default function MyJobsPage() {
  const navigate = useNavigate();
  const { providerUser, user } = useContext(UserContext) as any;
  const isLoggedIn = Boolean(providerUser?.email || user?.email);
  const seekerUserId = Number(providerUser?.userId || user?.id || 0) || null;
  const seekerEmail = String(providerUser?.email || user?.email || "").trim();
  const [tab, setTab] = React.useState<0 | 1 | 2>(0);
  const [membership, setMembership] = React.useState<ApplicantSubscriptionRecord | null>(null);
  const [membershipLoading, setMembershipLoading] = React.useState(false);

  // Load applications + saved jobs
  const appsQ = useQuery(MY_JOB_APPS, {
    fetchPolicy: "cache-and-network",
    skip: !isLoggedIn,
  });
  const savedQ = useQuery(MY_SAVED_JOBS, {
    fetchPolicy: "cache-and-network",
    skip: !isLoggedIn,
  });

  const apps = React.useMemo(() => appsQ.data?.myJobApplications ?? [], [appsQ.data]);
  const saved = React.useMemo(() => savedQ.data?.mySavedJobs ?? [], [savedQ.data]);

  // Build lookup of jobs already applied to
  const appliedIds = React.useMemo(() => {
    const set = new Set<number | string>();
    for (const a of apps) {
      const jid =
        a?.job?.id != null ? a.job.id : a?.jobId != null ? a.jobId : undefined;
      if (jid != null) set.add(typeof jid === "string" ? jid : Number(jid));
    }
    return set;
  }, [apps]);

  // Reusable job actions (apply/save/share). After save/unsave, refresh saved list.
  const { openApply, ApplyDialog, toggleSave } = useJobActions({
    afterSavedChange: () => savedQ.refetch?.(),
  });

  React.useEffect(() => {
    if (!isLoggedIn || (!seekerUserId && !seekerEmail)) {
      setMembership(null);
      return;
    }

    let active = true;
    setMembershipLoading(true);
    getLatestApplicantSubscription({ userId: seekerUserId, email: seekerEmail })
      .then((nextMembership) => {
        if (active) setMembership(nextMembership);
      })
      .catch(() => {
        if (active) setMembership(null);
      })
      .finally(() => {
        if (active) setMembershipLoading(false);
      });

    return () => {
      active = false;
    };
  }, [isLoggedIn, seekerEmail, seekerUserId]);

  if (!isLoggedIn) {
    return (
      <Box sx={{ maxWidth: 760, mx: "auto", px: 2, py: 3 }}>
        <Alert severity="info" sx={{ mb: 2 }}>
          Sign in to access your profile, saved jobs, applications, and free care job alerts.
        </Alert>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
          <Button
            variant="contained"
            onClick={() =>
              navigate(`/login?returnTo=${encodeURIComponent("/care/myjobs")}`)
            }
          >
            Sign in
          </Button>
          <Button variant="outlined" onClick={() => navigate("/care/jobs")}>
            Go to Job Board
          </Button>
        </Stack>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1000, mx: "auto", px: 2, py: 3 }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        sx={{ mb: 1.5 }}
      >
        <Typography variant="h5" fontWeight={800}>
          My Jobs & Profile
        </Typography>
        <Button
          size="small"
          variant="outlined"
          component={RouterLink}
          to="/care/jobs"
          sx={{ textTransform: "none", fontWeight: 700 }}
        >
          Go to Job Board
        </Button>
        <Button
          size="small"
          variant="outlined"
          component={RouterLink}
          to="/care/jobs/pricing"
          sx={{ textTransform: "none", fontWeight: 700 }}
        >
          Membership Pricing
        </Button>
      </Stack>

      {membershipLoading ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          Checking your membership...
        </Alert>
      ) : membership ? (
        <Alert severity={membership.status === "CANCELED" ? "warning" : "success"} sx={{ mb: 2 }}>
          Current membership:{" "}
          <strong>
            {membership.planName} {getApplicantSubscriptionStatusLabel(membership.status)}
          </strong>
          . You can manage or switch plans from the pricing page.
        </Alert>
      ) : (
        <Alert severity="info" sx={{ mb: 2 }}>
          You are using the free caregiver plan. Search and apply stay free, and you can
          add Standard or Premium from the pricing page any time.
        </Alert>
      )}

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        {/* ✅ fixed template strings */}
        <Tab label={`Applications (${apps.length})`} />
        <Tab label={`Saved (${saved.length})`} />
        <Tab label="Profile & Alerts" />
      </Tabs>

      {/* ===== Applications Tab ===== */}
      {tab === 0 ? (
        appsQ.error ? (
          <Alert severity="error">{String(appsQ.error.message)}</Alert>
        ) : apps.length === 0 ? (
          <Alert severity="info">No applications yet.</Alert>
        ) : (
          <Stack spacing={1.25}>
            {apps.map((app: any) => {
              const hasJob = !!app?.job;
              return (
                <Card
                  key={
                    app.id ??
                    `${app.userId}-${app.jobId}-${app.createdAt}`
                  }
                  variant="outlined"
                >
                  <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
                    {hasJob ? (
                      <>
                        {/* Already applied → hide Apply and Save */}
                        <JobCard
                          job={app.job}
                          status={app.status}
                          showShare
                          showApply={false}
                          showSave={false}
                        />
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ mt: 0.75, display: "block" }}
                        >
                          Applied: {fmtDateTime(app?.createdAt)}
                        </Typography>
                      </>
                    ) : (
                      <>
                        <Typography variant="h6">
                          Job no longer available
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          This posting may have been removed or closed.
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ mt: 0.75, display: "block" }}
                        >
                          Applied: {fmtDateTime(app?.createdAt)}
                        </Typography>
                      </>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </Stack>
        )
      ) : /* ===== Saved Tab ===== */ tab === 1 ? (
        savedQ.error ? (
          <Alert severity="error">{String(savedQ.error.message)}</Alert>
        ) : saved.length === 0 ? (
          <Alert severity="info">No saved jobs.</Alert>
        ) : (
          <Stack spacing={1.25}>
            {saved.map((job: any) => {
              const isApplied = appliedIds.has(job.id);
              return (
                <Card key={job.id} variant="outlined">
                  <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
                    {/* Hide Save on Saved tab; hide Apply if already applied */}
                    <JobCard
                      job={job}
                      showShare
                      showSave={false}
                      showApply={!isApplied}
                      onApply={openApply}
                    />
                    {isApplied && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ mt: 0.75, display: "block" }}
                      >
                        Already applied to this job.
                      </Typography>
                    )}
                    <Stack
                      direction="row"
                      justifyContent="flex-end"
                      sx={{ mt: 1 }}
                    >
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => toggleSave(job, false)}
                      >
                        Remove
                      </Button>
                    </Stack>
                  </CardContent>
                </Card>
              );
            })}
          </Stack>
        )
      ) : (
        /* ===== Profile Tab (backend) ===== */
        <JobProfileTab apps={apps} />
      )}

      {/* Apply dialog lives once */}
      {ApplyDialog}
    </Box>
  );
}
